import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray } from 'formik';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import _ from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import queryString from 'query-string';
import { cookie } from 'utils/cookies/cookies';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_WHATSNEW, UPDATE_WHATSNEW } from 'actions/whatsNew';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';

// CDB-1341 — two authoring modes. Group = one shared configuration for every
// slide; Individual = each slide scheduled, timed and targeted on its own.
// A story's mode is fixed once created (the window/duration/audience live in
// different places in each mode), so the selector is disabled while editing.
const MODE_GROUP = 'group';
const MODE_INDIVIDUAL = 'individual';

// CDB-1341 — stories may only be published to this app version or above. This is
// what keeps them out of the older /v1 flow, so it is validated here as well as
// on the server.
const MIN_APP_VERSION = '2.3.0';

// CDB-1341 — Template 3 is retired: template 2 with no media covers that layout.
// Existing template-3 records stay readable, they just can't be authored.
const TEMPLATE_OPTIONS = [
  { label: 'Template 1 — Media + title (media required)', value: 1 },
  { label: 'Template 2 — Title + points (media optional)', value: 2 },
];

const MEDIA_TYPE_OPTIONS = [
  { label: 'Image', value: 'image' },
  { label: 'GIF', value: 'gif' },
  { label: 'Video', value: 'video' },
  { label: 'Lottie animation', value: 'lottie' },
];

const ASPECT_RATIO_OPTIONS = [
  { label: '3:4 (portrait)', value: '3:4' },
  { label: '4:3 (landscape)', value: '4:3' },
  { label: '16:9 (wide)', value: '16:9' },
];

// CDB-1341 — the app resolves each key to a screen, so this list must match what
// the app can actually route to. CONFIRMED list (client, 2026-08-04); labels are
// the screen names as the client stated them. Keep in step with
// WHATS_NEW_CTA_DESTINATIONS in CMS-backend, which rejects anything not listed.
const CTA_DESTINATION_OPTIONS = [
  { label: 'Chargeclub page', value: 'CHARGE_CLUB' },
  { label: 'Chargeclub page - Redeem hubble', value: 'CHARGE_CLUB_REDEEM_HUBBLE' },
  { label: 'Home Page', value: 'HOME' },
  { label: 'Plan Trip', value: 'PLAN_TRIP' },
  { label: 'Sessions', value: 'SESSIONS' },
  { label: 'Wallet Page', value: 'WALLET' },
  { label: 'Profile Page', value: 'PROFILE' },
  { label: 'Add money to Wallet Page', value: 'WALLET_ADD_MONEY' },
  { label: 'Charge XP Page', value: 'CHARGE_XP' },
  { label: 'My EVs Page', value: 'MY_EVS' },
  { label: 'Account Page', value: 'ACCOUNT' },
  { label: 'RFID form', value: 'RFID_FORM' },
];

// Template 2 can present its points either as an icon list or as a numbered list
// (the layout template 3 used to provide). Which one is stored in the data
// itself — a bullet with a `step` is numbered — so this is a form-only choice,
// not a new field.
const BULLET_STYLE_OPTIONS = [
  { label: 'Icon list', value: 'icon' },
  { label: 'Numbered list', value: 'step' },
];

// Upload limits. The browser uploads straight to storage, so these are applied
// here, before the upload starts.
const MEDIA_RULES = {
  image: { accept: 'image/png,image/jpeg', maxBytes: 2 * 1024 * 1024, hint: 'PNG or JPG, up to 2 MB' },
  gif: { accept: 'image/gif', maxBytes: 2 * 1024 * 1024, hint: 'GIF, up to 2 MB' },
  video: { accept: 'video/mp4', maxBytes: 10 * 1024 * 1024, hint: 'MP4, up to 10 MB and 15 seconds' },
  lottie: { accept: 'application/json,.json', maxBytes: 512 * 1024, hint: 'Lottie JSON, up to 512 KB' },
};

const TIER_OPTIONS = [
  { label: 'Bronze', value: 'Bronze' },
  { label: 'Silver', value: 'Silver' },
  { label: 'Gold', value: 'Gold' },
  { label: 'Platinum', value: 'Platinum' },
  { label: 'Red', value: 'Red' },
];
const CUSTOMER_TYPE_OPTIONS = [
  { label: 'Customer', value: 'customer' },
  { label: 'Fleet Member', value: 'fleet_member' },
  { label: 'Fleet Owner', value: 'fleet_owner' },
];

// A file's real kind, taken from its extension. The Media Type dropdown is kept in
// step with this, so the stored type can never disagree with the stored file — a
// video saved as an image renders as a blank slide.
const MEDIA_TYPE_BY_EXTENSION = {
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'gif',
  mp4: 'video',
  json: 'lottie',
};

const typeFromUrl = (url) => {
  const ext = _.toLower(_.last(_.split(_.first(_.split(String(url || ''), '?')), '.')));
  return MEDIA_TYPE_BY_EXTENSION[ext] || null;
};

const emptySlide = {
  template: 1,
  title: '',
  description: '',
  bullets: [],
  bullet_style: 'icon',
  cta: { text: '', button_action: '' },
  // aspect_ratio sits inside media — it describes the media, and only applies to
  // video
  media: { type: 'image', url: '', thumbnail_url: '', aspect_ratio: '16:9' },
  // Form-only: files already uploaded for this slide, keyed by media type, so
  // changing type does not throw away an upload. Never sent to the API.
  media_uploads: {},
  duration_seconds: '',
  start_date: '',
  end_date: '',
};

// Compare dotted numeric versions part by part, so 2.10.0 correctly ranks above
// 2.3.0 (a string comparison gets that wrong).
const compareVersions = (a, b) => {
  const pa = String(a).split('.');
  const pb = String(b).split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = parseInt(pa[i], 10) || 0;
    const db = parseInt(pb[i], 10) || 0;
    if (da !== db) return da > db ? 1 : -1;
  }
  return 0;
};

const isValidVersion = (v) => /^\d+(\.\d+)*$/.test(_.trim(v || ''));

const WhatsNewForm = (props) => {
  const { isEdit, onClose = _.noop() } = props;
  const dispatch = useDispatch();

  const detail = useSelector((state) => state.whatsNew.whatsNewDetail);
  const isLoading = useSelector((state) => state.whatsNew.isLoading);

  // Pending story-type switch awaiting confirmation (see switchMode below).
  const [pendingSwitch, setPendingSwitch] = useState(null);

  // CDB-1341 — a story created before this change has no mode. Its content can no
  // longer be edited here: the update path copies fields on rather than replacing
  // the document, so saving new-format fields onto it would leave its old
  // single-value version field in place and the story would then reach older app
  // builds in a format they cannot render. It can still be switched off or
  // deleted from the list.
  const isLegacyRecord = isEdit && !_.get(detail, 'mode');

  // Presigned upload, then store the public URL. Size is checked first, because
  // the file goes straight to storage and not through our API.
  const uploadMedia = useCallback((acceptedFiles, rules, onDone) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    if (rules && file.size > rules.maxBytes) {
      // eslint-disable-next-line no-alert
      window.alert(`This file is too large. Allowed: ${rules.hint}.`);
      return;
    }
    const token = cookie.get('token');
    axios({
      method: 'get',
      headers: { Authorization: `Bearer ${token}` },
      url: `${process.env.REACT_APP_API_URL}/util/fileupload?name=${file.name}`,
    }).then((res) => {
      const signedRequest = _.get(res, 'data.signedRequest', '');
      if (!signedRequest) return;
      axios({ method: 'put', headers: { 'Content-Type': file.type }, url: signedRequest, data: file }).then((putRes) => {
        if (putRes.status === 200) {
          const urlData = queryString.parseUrl(signedRequest);
          onDone(urlData.url, file.name);
        }
      });
    });
  }, []);

  // Attach a media file to a slide. The type is taken from the file itself so the
  // dropdown can never end up disagreeing with what was uploaded, and the file is
  // remembered against that type so switching type does not lose it.
  const attachMedia = (index, url, selectedType, setFieldValue) => {
    const detected = typeFromUrl(url) || selectedType;
    setFieldValue(`slides[${index}].media.type`, detected);
    setFieldValue(`slides[${index}].media.url`, url);
    setFieldValue(`slides[${index}].media_uploads.${detected}`, url);
  };

  const create = useCallback((data) => {
    dispatch({
      type: CREATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  const update = useCallback((data) => {
    dispatch({
      type: UPDATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  // A story cannot be scheduled to start in the past. Only enforced when adding:
  // an existing story may legitimately have started days ago, and blocking that
  // would make it impossible to edit.
  const todayStr = moment().format('YYYY-MM-DD');
  const minStartDate = isEdit ? undefined : todayStr;

  const isNotInPast = (value) => {
    if (isEdit || !value) return true;
    return moment(value, 'YYYY-MM-DD').isSameOrAfter(moment().startOf('day'));
  };

  // Dates are required in both modes — at story level in Group, per slide in
  // Individual. `requiredMode` says which mode makes this field mandatory; the
  // other mode hides the field entirely, so it must not block submission there.
  // At story level the mode sits on `parent`; inside a slide it is one level up.
  const modeOf = (ctx) => _.get(ctx, 'parent.mode') || _.get(ctx, 'from[1].value.mode');

  const requiredStartDate = (requiredMode) =>
    Yup.string()
      .nullable()
      .test('start-required', 'Start date is required', function (value) {
        return modeOf(this) !== requiredMode || !!value;
      })
      .test('not-in-past', 'Start date cannot be in the past', isNotInPast);

  const requiredEndDate = (requiredMode, message) =>
    Yup.string()
      .nullable()
      .test('end-required', 'End date is required', function (value) {
        return modeOf(this) !== requiredMode || !!value;
      })
      .test('end-after-start', message, function (value) {
        const { start_date: start } = this.parent;
        if (!value || !start) return true;
        return new Date(value) >= new Date(start);
      });

  const dateRangeTest = (fromKey, message) =>
    Yup.string()
      .nullable()
      .test('to-after-from', message, function (value) {
        const from = this.parent[fromKey];
        if (!value || !from) return true; // either bound blank = unbounded
        return new Date(value) >= new Date(from);
      });

  const schema = Yup.object().shape({
    mode: Yup.string().required('Choose Group or Individual'),
    release_versions: Yup.array()
      .of(
        Yup.string()
          .trim()
          .required('App version is required')
          .test('version-format', 'Version must be numbers and dots only, e.g. 2.3.0', (v) => isValidVersion(v))
          .test(
            'version-floor',
            `Stories can only be published to app version ${MIN_APP_VERSION} or above`,
            (v) => !isValidVersion(v) || compareVersions(_.trim(v), MIN_APP_VERSION) >= 0
          )
      )
      .min(1, 'Add at least one app version'),
    priority: Yup.number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .integer('Priority must be a whole number')
      .min(1, 'Priority starts at 1')
      .nullable(),
    // Group mode: the shared window is mandatory here and hidden in Individual mode
    start_date: requiredStartDate(MODE_GROUP),
    end_date: requiredEndDate(MODE_GROUP, 'End date cannot be earlier than start date'),
    slide_duration_seconds: Yup.number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .min(0, 'Duration must be between 0 and 60 sec')
      .max(60, 'Duration must be between 0 and 60 sec')
      .nullable(),
    slides: Yup.array()
      .of(
        Yup.object().shape({
          title: Yup.string().trim().required('Slide title is required'),
          // Template 1 always carries media; template 2 may omit it
          media: Yup.object()
            .shape({
              url: Yup.string()
                .nullable()
                .test('media-required', 'Template 1 requires a media file', function (value) {
                  const template = Number(_.get(this, 'from[1].value.template'));
                  return template !== 1 || !!value;
                }),
            })
            .nullable(),
          duration_seconds: Yup.number()
            .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
            .min(0, 'Duration must be between 0 and 60 sec')
            .max(60, 'Duration must be between 0 and 60 sec')
            .nullable(),
          cta: Yup.object()
            .shape({
              button_action: Yup.string()
                .nullable()
                .test('cta-pair', 'Choose a destination for the button', function (value) {
                  const text = _.trim(_.get(this, 'parent.text', ''));
                  return !text || !!value;
                }),
              text: Yup.string()
                .nullable()
                .max(30, 'Button label can be up to 30 characters')
                .test('cta-pair-text', 'Add a label for the button', function (value) {
                  const link = _.get(this, 'parent.button_action', '');
                  return !link || !!_.trim(value || '');
                }),
            })
            .nullable(),
          // Individual mode: each slide carries its own mandatory window
          start_date: requiredStartDate(MODE_INDIVIDUAL),
          end_date: requiredEndDate(MODE_INDIVIDUAL, 'Slide end date cannot be earlier than its start date'),
          targeting: Yup.object()
            .shape({
              last_session_to: dateRangeTest('last_session_from', 'Last Session "To" cannot be earlier than "From"'),
              created_to: dateRangeTest('created_from', 'Account Created "To" cannot be earlier than "From"'),
            })
            .nullable(),
        })
      )
      .min(1, 'Add at least one slide'),
  });

  const asDate = (v) => (v ? moment(v).format('YYYY-MM-DD') : '');

  const initialValues = isEdit
    ? {
        id: _.get(detail, 'id', ''),
        mode: _.get(detail, 'mode', MODE_GROUP),
        release_versions: _.get(detail, 'release_versions', []).length ? _.get(detail, 'release_versions') : [''],
        priority: _.get(detail, 'priority', '') === null ? '' : _.get(detail, 'priority', ''),
        is_active: _.get(detail, 'is_active', false),
        ring: { label: _.get(detail, 'ring.label', "What's New") },
        start_date: asDate(_.get(detail, 'start_date')),
        end_date: asDate(_.get(detail, 'end_date')),
        slide_duration_seconds: _.get(detail, 'slide_duration_seconds', ''),
        targeting: _.get(detail, 'targeting')
          ? {
              ..._.get(detail, 'targeting'),
              last_session_from: asDate(_.get(detail, 'targeting.last_session_from')),
              last_session_to: asDate(_.get(detail, 'targeting.last_session_to')),
              created_from: asDate(_.get(detail, 'targeting.created_from')),
              created_to: asDate(_.get(detail, 'targeting.created_to')),
            }
          : undefined,
        slides: _.get(detail, 'slides', []).map((s) => ({
          template: _.get(s, 'template', 1),
          title: _.get(s, 'title', ''),
          description: _.get(s, 'description', ''),
          bullets: _.get(s, 'bullets', []),
          // derived from the data: a bullet carrying a step is a numbered list
          bullet_style: _.some(_.get(s, 'bullets', []), (b) => _.get(b, 'step') != null) ? 'step' : 'icon',
          cta: { text: _.get(s, 'cta.text', '') || '', button_action: _.get(s, 'cta.button_action', '') || '' },
          media: {
            type: _.get(s, 'media.type', 'image'),
            url: _.get(s, 'media.url', '') || '',
            thumbnail_url: _.get(s, 'media.thumbnail_url', '') || '',
            aspect_ratio: _.get(s, 'media.aspect_ratio', '16:9'),
          },
          // seed the remembered upload so switching type and back restores the
          // stored file rather than appearing to have lost it
          media_uploads:
            _.get(s, 'media.type') && _.get(s, 'media.url') ? { [_.get(s, 'media.type')]: _.get(s, 'media.url') } : {},
          duration_seconds: _.get(s, 'duration_seconds', '') === null ? '' : _.get(s, 'duration_seconds', ''),
          start_date: asDate(_.get(s, 'start_date')),
          end_date: asDate(_.get(s, 'end_date')),
          targeting: _.get(s, 'targeting')
            ? {
                ..._.get(s, 'targeting'),
                last_session_from: asDate(_.get(s, 'targeting.last_session_from')),
                last_session_to: asDate(_.get(s, 'targeting.last_session_to')),
                created_from: asDate(_.get(s, 'targeting.created_from')),
                created_to: asDate(_.get(s, 'targeting.created_to')),
              }
            : undefined,
        })),
      }
    : {
        // Individual is the default for a new story (client call 2026-08-04).
        mode: MODE_INDIVIDUAL,
        release_versions: [''],
        priority: '',
        is_active: false,
        ring: { label: "What's New" },
        start_date: '',
        end_date: '',
        slide_duration_seconds: '',
        slides: [{ ...emptySlide }],
      };

  // Build an audience object, or undefined when nothing is set so the story/slide
  // stays "shown to everyone".
  const buildTargeting = (tg) => {
    if (!tg) return undefined;
    const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const dat = (v) => v || null;
    const arr = (v) => (Array.isArray(v) && v.length ? v : undefined);
    const out = {
      tiers: arr(tg.tiers),
      customer_types: arr(tg.customer_types),
      wallet_min: num(tg.wallet_min),
      wallet_max: num(tg.wallet_max),
      last_session_from: dat(tg.last_session_from),
      last_session_to: dat(tg.last_session_to),
      created_from: dat(tg.created_from),
      created_to: dat(tg.created_to),
    };
    const hasAny =
      out.tiers ||
      out.customer_types ||
      out.wallet_min != null ||
      out.wallet_max != null ||
      out.last_session_from ||
      out.last_session_to ||
      out.created_from ||
      out.created_to;
    return hasAny ? out : undefined;
  };

  // CDB-1341 — build the payload from an explicit whitelist rather than spreading
  // the loaded record. The API rejects fields its schema doesn't declare, so
  // echoing back server-side fields (the old single-value version field,
  // timestamps, slide keys) would fail the request — and the old version field
  // must never be sent at all, since that is what keeps new stories out of the
  // older /v1 flow.
  const buildPayload = (values) => {
    const isIndividual = values.mode === MODE_INDIVIDUAL;
    const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

    const payload = {
      mode: values.mode,
      release_versions: _.compact(_.map(values.release_versions || [], (v) => _.trim(v))),
      is_active: !!values.is_active,
      ring: { label: _.get(values, 'ring.label', '') || null },
      slides: (values.slides || []).map((slide, idx) => {
        const template = Number(slide.template);
        const hasMedia = !!_.get(slide, 'media.url');
        const ctaText = _.trim(_.get(slide, 'cta.text', ''));
        const ctaLink = _.get(slide, 'cta.button_action', '');
        const numbered = _.get(slide, 'bullet_style') === 'step';

        const out = {
          order: idx + 1,
          template,
          title: slide.title,
          description: slide.description || null,
          // Template 1 is media + title only; it carries no points.
          bullets:
            template === 1
              ? []
              : (slide.bullets || [])
                  .filter((b) => _.trim(_.get(b, 'text', '')))
                  .map((b, bIdx) => ({
                    text: b.text,
                    icon: numbered ? null : b.icon || null,
                    step: numbered ? bIdx + 1 : null,
                  })),
          cta: ctaText && ctaLink ? { text: ctaText, button_action: ctaLink } : { text: null, button_action: null },
        };

        if (hasMedia) {
          out.media = {
            type: _.get(slide, 'media.type'),
            url: _.get(slide, 'media.url'),
            thumbnail_url: _.get(slide, 'media.thumbnail_url') || null,
          };
          // sent for video only — the API rejects it on other media types
          if (_.get(slide, 'media.type') === 'video') {
            out.media.aspect_ratio = _.get(slide, 'media.aspect_ratio') || null;
          }
        }

        // In Individual mode the window, duration and audience live on the slide.
        // In Group mode they live on the story, and the API rejects them here.
        if (isIndividual) {
          if (slide.start_date) out.start_date = slide.start_date;
          if (slide.end_date) out.end_date = slide.end_date;
          const duration = num(slide.duration_seconds);
          if (duration != null) out.duration_seconds = duration;
          const targeting = buildTargeting(slide.targeting);
          if (targeting) out.targeting = targeting;
        }
        return out;
      }),
    };

    const priority = num(values.priority);
    if (priority != null) payload.priority = priority;

    if (!isIndividual) {
      if (values.start_date) payload.start_date = values.start_date;
      if (values.end_date) payload.end_date = values.end_date;
      const duration = num(values.slide_duration_seconds);
      if (duration != null) payload.slide_duration_seconds = duration;
      const targeting = buildTargeting(values.targeting);
      if (targeting) payload.targeting = targeting;
    }

    if (isEdit) {
      payload.id = values.id;
      // the mode is fixed at creation and is not accepted on update
      delete payload.mode;
    }
    return payload;
  };

  // Real slide content — the work that would actually be lost. Scheduling fields
  // deliberately do NOT count: dates on their own are a few seconds to re-enter, and
  // confirming every time someone picks a date before settling on a type is noise.
  const hasSlideContent = (values) =>
    (values.slides || []).some(
      (s) => _.trim(_.get(s, 'title', '')) || _.trim(_.get(s, 'description', '')) || _.get(s, 'media.url')
    );

  // Apply the switch and clear the scheduling fields of the mode being left, so the
  // form matches what will actually be saved. Leaving them behind was misleading:
  // dates entered for Group are not saved on an Individual story, but stayed in the
  // form invisibly, so the warning appeared to have had no effect.
  // Content (versions, priority, ring label, and each slide's title, description,
  // media, button and points) is always kept — only the window, duration and
  // audience differ between the two modes.
  const applySwitch = (nextMode, values, setFieldValue) => {
    setFieldValue('mode', nextMode);
    if (nextMode === MODE_INDIVIDUAL) {
      setFieldValue('start_date', '');
      setFieldValue('end_date', '');
      setFieldValue('slide_duration_seconds', '');
      setFieldValue('targeting', undefined);
    } else {
      (values.slides || []).forEach((slide, i) => {
        setFieldValue(`slides[${i}].start_date`, '');
        setFieldValue(`slides[${i}].end_date`, '');
        setFieldValue(`slides[${i}].duration_seconds`, '');
        setFieldValue(`slides[${i}].targeting`, undefined);
      });
    }
  };

  // Confirm only when there is slide content to lose; otherwise switch straight
  // through. Either way the leaving mode's scheduling is cleared.
  const switchMode = (nextMode, values, setFieldValue) => {
    if (values.mode === nextMode) return;
    if (hasSlideContent(values)) {
      setPendingSwitch({
        from: values.mode,
        to: nextMode,
        apply: () => applySwitch(nextMode, values, setFieldValue),
      });
      return;
    }
    applySwitch(nextMode, values, setFieldValue);
  };

  // Audience block, reused for the story (Group mode) and each slide (Individual).
  const renderTargeting = ({ prefix, values, errors, touched, handleChange, setFieldValue }) => (
    <div className="whatsNew-targeting" style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 10 }}>
      <div className="form-label" style={{ fontWeight: 600 }}>
        Audience (optional)
      </div>
      <small style={{ color: '#888' }}>Leave everything blank to show this to all users. Any filter you set must all match.</small>
      <Row>
        <Col lg={6} md={6}>
          <Select
            label="Charge Club Tier"
            isMulti
            isClearable
            options={TIER_OPTIONS}
            placeholder="Any tier"
            value={_.get(values, `${prefix}.tiers`, [])}
            onChange={(val) => setFieldValue(`${prefix}.tiers`, val || [])}
          />
        </Col>
        <Col lg={6} md={6}>
          <Select
            label="Customer Type"
            isMulti
            isClearable
            options={CUSTOMER_TYPE_OPTIONS}
            placeholder="Any customer type"
            value={_.get(values, `${prefix}.customer_types`, [])}
            onChange={(val) => setFieldValue(`${prefix}.customer_types`, val || [])}
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Wallet Balance Min"
            type="number"
            name={`${prefix}.wallet_min`}
            value={_.get(values, `${prefix}.wallet_min`, '')}
            onChange={handleChange}
            onWheel={(e) => e.currentTarget.blur()}
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Wallet Balance Max"
            type="number"
            name={`${prefix}.wallet_max`}
            value={_.get(values, `${prefix}.wallet_max`, '')}
            onChange={handleChange}
            onWheel={(e) => e.currentTarget.blur()}
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Last Session From"
            type="date"
            name={`${prefix}.last_session_from`}
            value={_.get(values, `${prefix}.last_session_from`, '')}
            onChange={handleChange}
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Last Session To"
            type="date"
            name={`${prefix}.last_session_to`}
            min={_.get(values, `${prefix}.last_session_from`, '') || undefined}
            value={_.get(values, `${prefix}.last_session_to`, '')}
            onChange={handleChange}
            error={
              _.get(errors, `${prefix}.last_session_to`) && _.get(touched, `${prefix}.last_session_to`)
                ? _.get(errors, `${prefix}.last_session_to`)
                : null
            }
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Account Created From"
            type="date"
            name={`${prefix}.created_from`}
            value={_.get(values, `${prefix}.created_from`, '')}
            onChange={handleChange}
          />
        </Col>
        <Col lg={3} md={6}>
          <TextInput
            label="Account Created To"
            type="date"
            name={`${prefix}.created_to`}
            min={_.get(values, `${prefix}.created_from`, '') || undefined}
            value={_.get(values, `${prefix}.created_to`, '')}
            onChange={handleChange}
            error={
              _.get(errors, `${prefix}.created_to`) && _.get(touched, `${prefix}.created_to`)
                ? _.get(errors, `${prefix}.created_to`)
                : null
            }
          />
        </Col>
      </Row>
    </div>
  );

  if (isLegacyRecord) {
    return (
      <div className="faq-form-page__main">
        <Card>
          <div className="faq-form-header__block">
            <div className="faq-header-name">
              <span>What&apos;s New</span>
            </div>
            <div className="close___btn">
              <MdClose size={30} color="#be210b" onClick={onClose} />
            </div>
          </div>
          <div className="faq-form-body__block">
            <p style={{ marginBottom: 8 }}>
              This story was created before the What&apos;s New update, so its content can no longer be edited.
            </p>
            <p style={{ color: '#888' }}>
              You can still activate, deactivate or delete it from the list. To publish updated content, please create a new story.
            </p>
            <div className="form-btn__block" style={{ marginTop: 16 }}>
              <Button className="form_btn cancel____btn" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="faq-form-page__main whatsNew-form">
      {/* Hide the number-input stepper arrows. They sit right where the cursor
          lands and a stray click or scroll silently changes the value — the same
          reason every number field here also blurs on wheel. Scoped to this form
          so no other CMS screen changes. */}
      <style>
        {`.whatsNew-form input[type="number"]::-webkit-outer-spin-button,
          .whatsNew-form input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .whatsNew-form input[type="number"] { -moz-appearance: textfield; }`}
      </style>
      <Card>
        <div className="faq-form-header__block">
          <div className="faq-header-name">
            <span>{isEdit ? 'Edit' : 'Add'} What&apos;s New</span>
          </div>
          <div className="close___btn">
            <MdClose size={30} color="#be210b" onClick={onClose} />
          </div>
        </div>
        <div className="faq-form-body__block">
          <Formik
            enableReinitialize={!!isEdit}
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              const payload = buildPayload(values);
              if (isEdit) update(payload);
              else create(payload);
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting, submitCount }) => {
              const isIndividual = values.mode === MODE_INDIVIDUAL;
              // Show a field's error once the admin has touched it, or once they have
              // tried to submit. Without the second condition, errors on controls that
              // are never "touched" (a file picker, a select left at its default) would
              // never appear; without the first, an untouched blank form would open
              // already showing errors.
              const showError = (path) => (_.get(touched, path) || submitCount > 0 ? _.get(errors, path) : null);
              return (
                <Form onSubmit={handleSubmit}>
                  {/* ---- Mode: Group | Individual ---- */}
                  <div className="whatsNew-mode" style={{ marginBottom: 12 }}>
                    <div className="form-label" style={{ fontWeight: 600 }}>
                      Story type
                    </div>
                    <div className="d-flex" style={{ gap: 10 }}>
                      {/* Individual first, and selected by default — it is the
                          common case, so the form opens ready for it. */}
                      {[
                        { value: MODE_INDIVIDUAL, label: 'Individual' },
                        { value: MODE_GROUP, label: 'Group' },
                      ].map((tab) => {
                        const isSelected = values.mode === tab.value;
                        return (
                          <Button
                            key={tab.value}
                            type="button"
                            className="form_btn"
                            // The selected state is styled inline rather than by class: the
                            // themed button classes render almost identically here, which left
                            // the two tabs indistinguishable.
                            style={
                              isSelected
                                ? { opacity: 1, outline: '2px solid #fff', outlineOffset: 2, fontWeight: 700 }
                                : { opacity: 0.65, fontWeight: 400 }
                            }
                            disabled={isEdit}
                            onClick={() => switchMode(tab.value, values, setFieldValue)}
                          >
                            {tab.label}
                          </Button>
                        );
                      })}
                    </div>
                    <small style={{ color: '#888' }}>
                      {isIndividual
                        ? 'Individual — each slide has its own dates, duration and audience.'
                        : 'Group — one set of dates, duration and audience shared by every slide.'}
                      {isEdit ? ' The story type cannot be changed after creation.' : ''}
                    </small>
                  </div>

                  {/* Confirmation before changing story type.
                      Positioned with its own fixed-overlay styles rather than the CMS
                      modal classes: those rely on a DOM context this form does not
                      provide, so they rendered inline in the page instead of over it.
                      The box itself is a themed Card, so it still matches the CMS. */}
                  {pendingSwitch && (
                    <div
                      role="presentation"
                      onClick={() => setPendingSwitch(null)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.65)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: 16,
                      }}
                    >
                      <div
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%', maxWidth: 540 }}
                      >
                        <Card>
                          <div style={{ padding: 24 }}>
                            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Change story type?</div>
                            <p style={{ marginBottom: 20, lineHeight: 1.6 }}>
                              Each story is saved as one type — Group or Individual, not both. You have already entered data
                              here. To keep it as {pendingSwitch.from === MODE_GROUP ? 'a Group' : 'an Individual'} story, save
                              it first, then add a new story for{' '}
                              <strong>{pendingSwitch.to === MODE_GROUP ? 'Group' : 'Individual'}</strong>. Switching now turns
                              this same story into {pendingSwitch.to === MODE_GROUP ? 'a Group' : 'an Individual'} one — it does
                              not create a second story.
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                              <Button className="form_btn cancel____btn" onClick={() => setPendingSwitch(null)}>
                                Go back and save
                              </Button>
                              <Button
                                className="form_btn"
                                onClick={() => {
                                  pendingSwitch.apply();
                                  setPendingSwitch(null);
                                }}
                              >
                                Switch to {pendingSwitch.to === MODE_GROUP ? 'Group' : 'Individual'}
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    </div>
                  )}

                  {/* ---- Story-level config ---- */}
                  <Row>
                    <Col lg={12}>
                      <div className="form-label" style={{ fontWeight: 600 }}>
                        App versions <span style={{ color: '#be210b' }}>*</span>
                      </div>
                      <small style={{ color: '#888' }}>
                        Only users on a listed version see this story. Version {MIN_APP_VERSION} or above.
                      </small>
                      <FieldArray name="release_versions">
                        {({ push: pushVersion, remove: removeVersion }) => (
                          <div>
                            {_.map(_.get(values, 'release_versions', []), (version, vIndex) => (
                              <Row key={`version-${vIndex}`} className="align-items-center">
                                <Col lg={4} md={6}>
                                  <TextInput
                                    placeholder="e.g. 2.3.0"
                                    name={`release_versions[${vIndex}]`}
                                    value={version || ''}
                                    onChange={handleChange}
                                    error={
                                      _.get(errors, `release_versions[${vIndex}]`) && _.get(touched, `release_versions[${vIndex}]`)
                                        ? _.get(errors, `release_versions[${vIndex}]`)
                                        : null
                                    }
                                  />
                                </Col>
                                <Col lg={2} md={3}>
                                  {_.get(values, 'release_versions', []).length > 1 && (
                                    <MdClose
                                      size={20}
                                      color="#be210b"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => removeVersion(vIndex)}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                            <Button type="button" className="form_btn" onClick={() => pushVersion('')}>
                              + Add version
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                    </Col>
                  </Row>

                  <Row style={{ marginTop: 12 }}>
                    <Col lg={4} md={6}>
                      <TextInput
                        label="Priority (optional)"
                        type="number"
                        min={1}
                        name="priority"
                        placeholder="1 shows first"
                        value={_.get(values, 'priority', '')}
                        onChange={handleChange}
                        onWheel={(e) => e.currentTarget.blur()}
                        error={errors.priority && touched.priority ? errors.priority : null}
                      />
                    </Col>
                    <Col lg={4} md={6}>
                      <TextInput
                        label="Ring Label"
                        placeholder="What's New"
                        name="ring.label"
                        value={_.get(values, 'ring.label', '')}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  {/* Group mode: the shared window, duration and audience */}
                  {!isIndividual && (
                    <>
                      <Row>
                        <Col lg={4} md={6}>
                          <TextInput
                            label="Start Date"
                            type="date"
                            name="start_date"
                            min={minStartDate}
                            value={_.get(values, 'start_date', '')}
                            onChange={handleChange}
                            error={showError('start_date')}
                          />
                        </Col>
                        <Col lg={4} md={6}>
                          <TextInput
                            label="End Date"
                            type="date"
                            name="end_date"
                            min={_.get(values, 'start_date', '') || minStartDate}
                            value={_.get(values, 'end_date', '')}
                            onChange={handleChange}
                            error={errors.end_date && touched.end_date ? errors.end_date : null}
                          />
                        </Col>
                        <Col lg={4} md={6}>
                          <TextInput
                            label="Slide Duration (sec, 0–60)"
                            type="number"
                            min={0}
                            max={60}
                            placeholder="5 sec by default if left blank"
                            name="slide_duration_seconds"
                            value={_.get(values, 'slide_duration_seconds', '')}
                            onChange={handleChange}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={
                              errors.slide_duration_seconds && touched.slide_duration_seconds ? errors.slide_duration_seconds : null
                            }
                          />
                        </Col>
                      </Row>
                      {renderTargeting({ prefix: 'targeting', values, errors, touched, handleChange, setFieldValue })}
                    </>
                  )}

                  <div className="form-label" style={{ marginTop: 16, fontWeight: 600 }}>
                    Slides
                  </div>

                  {/* ---- Slides ---- */}
                  <FieldArray name="slides">
                    {({ push, remove }) => (
                      <div className="whatsNew-slides">
                        {_.map(_.get(values, 'slides', []), (slide, index) => {
                          const template = Number(_.get(slide, 'template'));
                          const mediaType = _.get(slide, 'media.type', 'image');
                          const rules = MEDIA_RULES[mediaType] || MEDIA_RULES.image;
                          return (
                            <Card key={`slide-${index}`} className="mb-3" style={{ padding: 12, marginTop: 12 }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <strong>Slide {index + 1}</strong>
                                {values.slides.length > 1 && (
                                  <MdClose size={22} color="#be210b" style={{ cursor: 'pointer' }} onClick={() => remove(index)} />
                                )}
                              </div>
                              <Row>
                                <Col lg={4} md={6}>
                                  <Select
                                    label="Template"
                                    options={TEMPLATE_OPTIONS}
                                    value={_.get(slide, 'template', 1)}
                                    onChange={(val) => setFieldValue(`slides[${index}].template`, val)}
                                  />
                                </Col>
                                <Col lg={4} md={6}>
                                  <TextInput
                                    isRequired
                                    label="Title"
                                    name={`slides[${index}].title`}
                                    value={_.get(slide, 'title', '')}
                                    onChange={handleChange}
                                    error={
                                      _.get(errors, `slides[${index}].title`) && _.get(touched, `slides[${index}].title`)
                                        ? _.get(errors, `slides[${index}].title`)
                                        : null
                                    }
                                  />
                                </Col>
                                <Col lg={12}>
                                  <TextInput
                                    as="textarea"
                                    rows="3"
                                    label="Description"
                                    name={`slides[${index}].description`}
                                    value={_.get(slide, 'description', '')}
                                    onChange={handleChange}
                                  />
                                </Col>
                              </Row>

                              {/* ---- Media ---- */}
                              <Row>
                                <Col lg={4} md={6}>
                                  <Select
                                    label="Media Type"
                                    options={MEDIA_TYPE_OPTIONS}
                                    value={mediaType}
                                    onChange={(val) => {
                                      // Show whatever was already uploaded for the type being
                                      // switched to, if anything. A file uploaded for another
                                      // type is not valid here, but it is remembered rather
                                      // than discarded, so switching back restores it.
                                      setFieldValue(`slides[${index}].media.type`, val);
                                      setFieldValue(`slides[${index}].media.url`, _.get(slide, `media_uploads.${val}`, '') || '');
                                    }}
                                  />
                                </Col>
                                {/* Aspect ratio applies to video only — the player needs to be
                                    sized, whereas an image, GIF or Lottie is laid out from the
                                    asset itself. */}
                                {mediaType === 'video' && (
                                  <Col lg={4} md={6}>
                                    <Select
                                      label="Aspect Ratio"
                                      options={ASPECT_RATIO_OPTIONS}
                                      value={_.get(slide, 'media.aspect_ratio', '16:9')}
                                      onChange={(val) => setFieldValue(`slides[${index}].media.aspect_ratio`, val)}
                                    />
                                  </Col>
                                )}
                                <Col lg={12}>
                                  <label className="form-label" htmlFor={`slide-media-${index}`}>
                                    Media {template === 1 ? <span style={{ color: '#be210b' }}>*</span> : '(optional)'}
                                  </label>
                                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                    <input
                                      id={`slide-media-${index}`}
                                      type="file"
                                      accept={rules.accept}
                                      onChange={(e) =>
                                        uploadMedia(e.target.files, rules, (url) =>
                                          attachMedia(index, url, mediaType, setFieldValue)
                                        )
                                      }
                                    />
                                    {_.get(slide, 'media.url') && (
                                      <>
                                        {['image', 'gif'].includes(mediaType) && (
                                          <img
                                            src={_.get(slide, 'media.url')}
                                            alt="attached media"
                                            style={{ height: 40, borderRadius: 4 }}
                                          />
                                        )}
                                        <a href={_.get(slide, 'media.url')} target="_blank" rel="noreferrer">
                                          {_.last(_.split(_.get(slide, 'media.url'), '/'))}
                                        </a>
                                        <Button
                                          type="button"
                                          className="form_btn cancel____btn"
                                          onClick={() => {
                                            setFieldValue(`slides[${index}].media.url`, '');
                                            setFieldValue(`slides[${index}].media_uploads.${mediaType}`, '');
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                  <small style={{ color: '#888' }}>{rules.hint}</small>
                                  {showError(`slides[${index}].media.url`) && (
                                    <div style={{ color: '#be210b', fontSize: 12 }}>
                                      {showError(`slides[${index}].media.url`)}
                                    </div>
                                  )}
                                </Col>
                                {mediaType === 'video' && (
                                  <Col lg={12}>
                                    <label className="form-label" htmlFor={`slide-thumb-${index}`}>
                                      Video thumbnail (optional — shown before the clip plays)
                                    </label>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                      <input
                                        id={`slide-thumb-${index}`}
                                        type="file"
                                        accept={MEDIA_RULES.image.accept}
                                        onChange={(e) =>
                                          uploadMedia(e.target.files, MEDIA_RULES.image, (url) =>
                                            setFieldValue(`slides[${index}].media.thumbnail_url`, url)
                                          )
                                        }
                                      />
                                      {_.get(slide, 'media.thumbnail_url') && (
                                        <img
                                          src={_.get(slide, 'media.thumbnail_url')}
                                          alt="thumbnail"
                                          style={{ height: 40, borderRadius: 4 }}
                                        />
                                      )}
                                    </div>
                                  </Col>
                                )}
                              </Row>

                              {/* ---- Action button (optional, any template) ---- */}
                              <Row>
                                <Col lg={4} md={6}>
                                  <TextInput
                                    label="Button Label (optional)"
                                    placeholder="e.g. Charge now"
                                    name={`slides[${index}].cta.text`}
                                    value={_.get(slide, 'cta.text', '')}
                                    onChange={handleChange}
                                    error={
                                      _.get(errors, `slides[${index}].cta.text`) && _.get(touched, `slides[${index}].cta.text`)
                                        ? _.get(errors, `slides[${index}].cta.text`)
                                        : null
                                    }
                                  />
                                </Col>
                                <Col lg={4} md={6}>
                                  <Select
                                    label="Button Destination"
                                    isClearable
                                    options={CTA_DESTINATION_OPTIONS}
                                    placeholder="No button"
                                    value={_.get(slide, 'cta.button_action', '')}
                                    onChange={(val) => setFieldValue(`slides[${index}].cta.button_action`, val || '')}
                                  />
                                  {showError(`slides[${index}].cta.button_action`) && (
                                    <div style={{ color: '#be210b', fontSize: 12 }}>
                                      {showError(`slides[${index}].cta.button_action`)}
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              {/* ---- Points — template 2 only ---- */}
                              {template === 2 && (
                                <FieldArray name={`slides[${index}].bullets`}>
                                  {({ push: pushBullet, remove: removeBullet }) => (
                                    <div className="whatsNew-bullets" style={{ marginTop: 8 }}>
                                      <Row>
                                        <Col lg={4} md={6}>
                                          <Select
                                            label="Points style"
                                            options={BULLET_STYLE_OPTIONS}
                                            value={_.get(slide, 'bullet_style', 'icon')}
                                            onChange={(val) => setFieldValue(`slides[${index}].bullet_style`, val)}
                                          />
                                        </Col>
                                      </Row>
                                      {_.map(_.get(slide, 'bullets', []), (bullet, bIndex) => (
                                        <Row key={`bullet-${index}-${bIndex}`} className="align-items-center">
                                          <Col lg={10} md={9}>
                                            <TextInput
                                              placeholder={`Point ${bIndex + 1}`}
                                              name={`slides[${index}].bullets[${bIndex}].text`}
                                              value={_.get(bullet, 'text', '')}
                                              onChange={handleChange}
                                            />
                                          </Col>
                                          <Col lg={2} md={3}>
                                            <MdClose
                                              size={20}
                                              color="#be210b"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() => removeBullet(bIndex)}
                                            />
                                          </Col>
                                        </Row>
                                      ))}
                                      <Button type="button" className="form_btn" onClick={() => pushBullet({ text: '', icon: '' })}>
                                        + Add point
                                      </Button>
                                    </div>
                                  )}
                                </FieldArray>
                              )}
                              {/* Individual mode: this slide's own window, duration and audience */}
                              {isIndividual && (
                                <>
                                  <Row>
                                    <Col lg={4} md={6}>
                                      <TextInput
                                        label="Slide Start Date"
                                        type="date"
                                        name={`slides[${index}].start_date`}
                                        min={minStartDate}
                                        value={_.get(slide, 'start_date', '')}
                                        onChange={handleChange}
                                        error={showError(`slides[${index}].start_date`)}
                                      />
                                    </Col>
                                    <Col lg={4} md={6}>
                                      <TextInput
                                        label="Slide End Date"
                                        type="date"
                                        name={`slides[${index}].end_date`}
                                        min={_.get(slide, 'start_date', '') || minStartDate}
                                        value={_.get(slide, 'end_date', '')}
                                        onChange={handleChange}
                                        error={showError(`slides[${index}].end_date`)}
                                      />
                                    </Col>
                                    <Col lg={4} md={6}>
                                      <TextInput
                                        label="Slide Duration (sec, 0–60)"
                                        type="number"
                                        min={0}
                                        max={60}
                                        placeholder="5 sec by default if left blank"
                                        name={`slides[${index}].duration_seconds`}
                                        value={_.get(slide, 'duration_seconds', '')}
                                        onChange={handleChange}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        error={
                                          _.get(errors, `slides[${index}].duration_seconds`) &&
                                          _.get(touched, `slides[${index}].duration_seconds`)
                                            ? _.get(errors, `slides[${index}].duration_seconds`)
                                            : null
                                        }
                                      />
                                    </Col>
                                  </Row>
                                  {renderTargeting({
                                    prefix: `slides[${index}].targeting`,
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    setFieldValue,
                                  })}
                                </>
                              )}

                            </Card>
                          );
                        })}
                        <Button type="button" className="form_btn" onClick={() => push({ ...emptySlide })}>
                          + Add slide
                        </Button>
                      </div>
                    )}
                  </FieldArray>

                  <div className="form-btn__block" style={{ marginTop: 16 }}>
                    <Button className="form_btn cancel____btn" style={{ marginRight: 16 }} onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || isLoading} className="form_btn">
                      {isEdit ? 'Update' : 'Add'} What&apos;s New
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

WhatsNewForm.propTypes = {
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default WhatsNewForm;
